<template>
  <div class="d-flex flex-column justify-content-end w-100">
    <label
      v-if="itemModel.vars.label"
      :class="
        'form-label' +
        (itemModel.vars.label_attr && itemModel.vars.label_attr.class ? ' ' + itemModel.vars.label_attr.class : '') +
        (itemModel.vars.required ? ' required' : '')
      "
    >
      {{ index ? index + ". " : "" }}{{ itemModel.vars.label }}{{ itemModel.vars.required ? "*" : "" }}
      <span
        v-if="itemModel.vars.help && !isMobile()"
        v-tooltip="{ title: itemModel.vars.help, textWrap: true }"
        class="form-info ms-1"
      >
        <FontAwesomeIcon icon="fa-light fa-circle-question" />
      </span>
    </label>
    <div v-if="isMobile() && itemModel.vars.help" class="form-info">
      <FontAwesomeIcon icon="fa-light fa-circle-question" />
      {{ itemModel.vars.help }}
    </div>
    <div v-if="isPasswordType()" class="position-relative d-flex">
      <PasswordTypeComponent
        :password="itemModel.vars.value"
        :model="itemModel"
        :itemClass="itemClass"
        :displayInfo="itemModel.vars.attr['displayInfo']"
        @changePassword="blur"
        @checkInput="change"
      />
    </div>
    <div
      v-else
      :class="{
        'position-relative': itemModel.vars.block_prefixes[1] === 'number',
        'w-100': true,
        'd-flex align-items-center ': itemModel?.vars?.attr?.['data-button'],
      }"
    >
      <input
        v-if="!itemModel.vars.hide"
        :id="itemModel.vars.id"
        v-model="itemModel.vars.value"
        :readonly="itemModel.vars.attr && itemModel.vars.attr.readonly"
        :class="
          itemClass +
          ' ' +
          (itemModel.vars.attr && itemModel.vars.attr.class ? itemModel.vars.attr.class : '') +
          (itemModel.vars?.attr?.currency ? ' currency' : '')
        "
        :disabled="itemModel.vars.disabled === true"
        :name="language ? itemModel.vars.name + '-' + language : itemModel.vars.name"
        :placeholder="itemModel?.vars?.attr?.placeholder ?? ''"
        :required="itemModel.vars.required === true"
        :type="itemModel.vars.block_prefixes[1]"
        :maxlength="itemModel?.vars?.attr?.maxlength ?? ''"
        @blur="blur"
        @change="change"
        @focus="focus"
        @input="input"
      />
      <div class="input__operator">
        <div v-if="displayOperatorButton()" class="input__nav">
          <button type="button" @click="stepUp">
            <FontAwesomeIcon icon="fa-light fa-plus" />
          </button>
          <button type="button" @click="stepDown">
            <FontAwesomeIcon icon="fa-light fa-minus" />
          </button>
        </div>
        <div v-if="itemModel.vars?.attr?.currency" class="input__currency">
          {{ itemModel.vars?.attr?.currency }}
        </div>
      </div>
      <button
        v-if="itemModel?.vars?.attr?.['data-button']"
        v-tooltip="{ title: $t('regenerate') }"
        type="button"
        class="ms-2 btn btn-success"
        @click="buttonAction()"
      >
        <FontAwesomeIcon
          v-if="itemModel.vars.attr['data-button-icon']"
          :icon="'fa-light fa-' + itemModel.vars.attr['data-button-icon']"
        />
      </button>
    </div>
    <div v-if="model.vars.error || errors" class="invalid-feedback d-block">
      {{ model.vars.error }}
      {{ errors }}
    </div>
  </div>
</template>

<script>
import { formValidator } from "@/services/form/form-validator";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import PasswordTypeComponent from "@/components/form/PasswordTypeComponent.vue";
import { stringManager } from "@/services/utilities/string-manager";

export default {
  name: "InputTypeComponent",
  components: {
    PasswordTypeComponent,
    FontAwesomeIcon,
  },
  props: {
    model: {
      type: Object,
      required: true,
    },
    language: {
      type: String,
      required: false,
      default: "fr",
    },
    index: {
      type: Number,
      required: false,
      default: null,
    },
    forcedDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ["input", "draft", "focus", "update", "focused", "save", "buttonAction"],
  data() {
    return {
      itemModel: this.model,
      itemClass: "form-control",
      errors: null,
    };
  },
  watch: {
    "model.vars.error"() {
      let errors = [];
      if (this.model.vars.error) {
        errors = [this.model.vars.error];
      }
      this.getClass(errors);
    },
    "model.vars.isInvalid"() {
      let errors = [];
      if (this.model.vars.error) {
        errors = [this.model.vars.error];
      }
      this.getClass(errors);
    },
    model() {
      this.itemModel.vars.value = this.model?.vars?.value;
    },
  },
  mounted() {
    if (
      this.itemModel.vars.block_prefixes[1] === "number" &&
      this.itemModel.vars.value &&
      this.itemModel.vars.value.length
    ) {
      this.itemModel.vars.value = parseFloat(this.itemModel?.vars?.value?.replace(",", "."));
    }
  },
  methods: {
    getClass(errors = []) {
      if (this.model.vars.error || errors.length || this.model.vars.isInvalid) {
        this.itemClass = "form-control is-invalid";
      } else if (!errors.length && !this.model.vars.error && this.model.vars.required) {
        this.itemClass = "form-control is-valid";
      } else {
        this.itemClass = "form-control";
      }
    },
    blur() {
      this.valid(true);
      this.$emit("update", this.itemModel.vars.value, this.index);
    },
    input() {
      this.formatNafCode();
      this.valid();
      this.$emit("update", this.itemModel.vars.value, this.index);
      this.$emit("input", this.itemModel.vars.value, this.index);
      this.$emit("inputModel", this.itemModel);
    },
    change() {
      this.valid();
      this.$emit("draft", this.itemModel);
      this.$emit("update", this.itemModel.vars.value, this.index);
    },
    focus() {
      this.$emit("focus");
    },
    isMobile() {
      return window.innerWidth < 992;
    },
    valid(blur = false) {
      this.enforceDigit(this.itemModel.vars);
      if (blur) {
        const value = this.itemModel?.vars?.value;
        if (value != null) {
          this.itemModel.vars.value = String(value).trim();
        }
        this.$emit("save");
      }
      const errors = formValidator.validModel(this.itemModel);
      this.getClass(errors);
      formValidator.handleNumberError(this.itemModel);
    },

    stepUp() {
      this.itemModel.vars.value++;
      this.valid();
      this.$emit("update", this.itemModel.vars.value, this.index);
      this.$emit("input", this.itemModel.vars.value, this.index);
      this.$emit("save");
      this.change();
    },

    stepDown() {
      this.itemModel.vars.value--;
      this.valid();
      this.$emit("update", this.itemModel.vars.value, this.index);
      this.$emit("input", this.itemModel.vars.value, this.index);
      this.$emit("save");
      this.change();
    },

    isPasswordType() {
      return this.itemModel.vars.block_prefixes[1] === "text" && this.itemModel.vars.block_prefixes[2] === "password";
    },
    formatNafCode() {
      if (this.itemModel.vars.name === "nafCode") {
        let val = this.itemModel.vars.value;

        if (val.length > 2 && val[2] !== ".") {
          this.itemModel.vars.value = val.slice(0, 2) + "." + val.slice(2);
        }
      }
    },

    enforceDigit(vars) {
      if (
        vars &&
        vars.value &&
        vars.value !== "" &&
        vars.block_prefixes[1] === "number" &&
        vars.attr &&
        vars.attr["data-max-digit"]
      ) {
        vars.value = stringManager.enforceDigit(vars.value, vars.attr["data-max-digit"]);
      }
    },

    displayOperatorButton() {
      return (
        this.itemModel?.vars?.block_prefixes[1] === "number" &&
        !this.itemModel?.vars?.hide &&
        !this.itemModel?.vars?.disabled &&
        !this.itemModel?.vars?.attr?.["hideOperatorButtons"]
      );
    },

    buttonAction() {
      this.$emit("buttonAction", this.itemModel?.vars?.attr["data-button"]);
    },
  },
};
</script>

<style lang="scss" scoped>
.non-empty {
  overflow: auto;
}

input[type="number"] {
  -webkit-appearance: textfield;
  -moz-appearance: textfield;
  appearance: textfield;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
}

.input__operator {
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
}

.input__nav {
  display: flex;
  padding: 10px 0;

  button {
    border: none;
    background: none;
    color: #3b3f5c;
    display: flex;
    align-items: center;
    padding: 5px 10px;

    @include dark-theme {
      color: $white;
    }

    &:hover {
      color: var(--primary-color);
    }

    &:first-child {
      border-right: 1px solid $grey;
    }
  }
}

.input__currency {
  width: 40px;
  height: 100%;
  background: $m_color_3;
  border-left: none;
  border-radius: 0 $radius $radius 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;

  @include dark-theme {
    background: $dark-theme-bg;
    border-left: 1px solid $m-color_12;
  }
}

input[type="number"] {
  &.form-control.is-valid,
  &.form-control.is-invalid {
    background: $white;
  }
}
</style>
